import React, { useRef, useState } from "react";
import SmartTooltip from "../Info/SmartTooltip";

const sizeMap = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
};

const badgeIconFontSizeMap = {
  xs: 9,
  sm: 10,
  md: 11,
  lg: 12,
  xl: 15,
};

const DEFAULT_DOT_COLOR = "bg-success";

const DualIcon = ({
  icon = "chat",
  size = "md",
  iconColor = "warning-400",
  badgeIconColor = DEFAULT_DOT_COLOR,
  dotPosition = "top-right",
  badgeIcon = null,
  onClick,
  // SmartTooltip props
  showTooltip = false, // Enable/disable tooltip feature
  tooltipDescription = null, // Tooltip text
  tooltipDelay = 2000, // Delay in milliseconds before showing tooltip
  tooltipSize = "xs",
  tooltipColor = "black",
  tooltipPosition = "right",
  tooltipVariant = "contained",
  tooltipOffset = 18,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showDelayedTooltip, setShowDelayedTooltip] = useState(false);
  const hoverTimerRef = useRef(null);

  const iconPx = sizeMap[size] || sizeMap["md"];
  const badgeIconFontSize = badgeIconFontSizeMap[size] || 14;
  const dotSize = iconPx * 0.25;
  const padding = badgeIcon ? 3 : 2;
  const wrapperSize = dotSize + padding * 2;

  const nudge = iconPx * 0.2;

  const positionStyle = (() => {
    switch (dotPosition) {
      case "top-right":
        return {
          top: 0,
          right: 0,
          transform: `translate(50%, -50%) translate(-${nudge}px, ${nudge}px)`,
        };
      case "bottom-right":
        return {
          bottom: 2,
          right: 0,
          transform: `translate(50%, 50%) translate(-${nudge}px, -${nudge}px)`,
        };
      default:
        return {
          top: 0,
          right: 0,
          transform: `translate(50%, -50%) translate(-${nudge}px, ${nudge}px)`,
        };
    }
  })();

  const isUsingDefaultColor = badgeIconColor === DEFAULT_DOT_COLOR;
  const finalBadgeIconTextColor = isUsingDefaultColor
    ? `text-${iconColor}`
    : `text-${badgeIconColor}`;

  const handleMouseEnter = () => {
    setIsHovering(true);

    if (!showTooltip || !tooltipDescription) return;

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    hoverTimerRef.current = setTimeout(() => {
      setShowDelayedTooltip(true);
    }, tooltipDelay);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShowDelayedTooltip(false);
  };

  const dualIconContent = (
    <div
      className="relative inline-block leading-none my-2 mx-1 cursor-pointer"
      style={{ width: iconPx, height: iconPx }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main Icon */}
      <span
        className={`material-symbols-outlined block text-${iconColor}`}
        style={{ fontSize: iconPx }}
      >
        {icon}
      </span>

      {/* Badge Wrapper */}
      <div
        className="absolute flex items-center justify-center bg-white rounded-full"
        style={{
          width: wrapperSize,
          height: wrapperSize,
          ...positionStyle,
        }}
      >
        {!badgeIcon ? (
          <div
            className={`rounded-full ${badgeIconColor}`}
            style={{
              width: dotSize,
              height: dotSize,
            }}
          />
        ) : (
          <span
            className={`material-icons ${finalBadgeIconTextColor}`}
            style={{
              fontSize: badgeIconFontSize,
              lineHeight: 1,
            }}
          >
            {badgeIcon}
          </span>
        )}
      </div>
    </div>
  );

  // If tooltip is enabled and description is provided, wrap with SmartTooltip
  if (showTooltip && tooltipDescription) {
    return (
      <SmartTooltip
        description={tooltipDescription}
        size={tooltipSize}
        color={tooltipColor}
        tooltipPosition={tooltipPosition}
        variant={tooltipVariant}
        delay={0} // We handle delay ourselves
        offset={tooltipOffset}
        noicon={true}
        noTooltip={!showDelayedTooltip} // Only show tooltip after delay
      >
        {dualIconContent}
      </SmartTooltip>
    );
  }
  return dualIconContent;
};

export default DualIcon;
